import React, { useState, useMemo, useEffect, useRef } from 'react';
import { MatterDetailed } from '../../../types/matters';
import { highlightText } from '../../../utils/textHighlight';
import { useKeycloak } from '@react-keycloak/web';

interface MattersTableViewProps {
  matters: MatterDetailed[];
  loading: boolean;
  onMatterClick: (matter: MatterDetailed) => void;
  searchQuery?: string;
  onRefreshNeeded?: () => Promise<void>;
}

interface NextScheduledEvents {
  [matterId: string]: {
    nextCourt?: { title: string; start_time: string; location?: string };
    nextMeeting?: { title: string; start_time: string; location?: string };
  };
}

interface MatterEnrichment {
  task_summary: { total: number; done: number; overdue: number };
  budget: { total_budget: number; status: string } | null;
  retainer_balance: number | null;
  risk_level: string | null;
}

interface EnrichmentData {
  [matterId: string]: MatterEnrichment;
}

// --- Helper functions ---

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    case 'on_hold': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  }
};

const getPriorityConfig = (priority: string | undefined) => {
  switch (priority) {
    case 'urgent': return { badge: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400', label: 'Urgent' };
    case 'high': return { badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400', label: 'High' };
    case 'medium': return { badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400', label: 'Medium' };
    case 'low': return { badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400', label: 'Low' };
    default: return { badge: 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500', label: '—' };
  }
};

const getRiskConfig = (level: string | null | undefined) => {
  switch (level) {
    case 'critical': return { color: 'bg-red-600', label: 'Critical' };
    case 'high': return { color: 'bg-red-500', label: 'High' };
    case 'medium': return { color: 'bg-amber-500', label: 'Med' };
    case 'low': return { color: 'bg-green-500', label: 'Low' };
    case 'minimal': return { color: 'bg-green-400', label: 'Min' };
    default: return { color: 'bg-gray-300 dark:bg-gray-600', label: '—' };
  }
};

const getAgeDays = (openDate: string): number => {
  const open = new Date(openDate);
  const now = new Date();
  return Math.max(0, Math.floor((now.getTime() - open.getTime()) / (1000 * 60 * 60 * 24)));
};

const getDaysUntilSOL = (solDate: string | undefined): number | null => {
  if (!solDate) return null;
  const sol = new Date(solDate);
  const now = new Date();
  return Math.ceil((sol.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatEventDate = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === today.toDateString()) return `Today ${time}`;
  if (date.toDateString() === tomorrow.toDateString()) return `Tomorrow ${time}`;
  return `${date.toLocaleDateString()} ${time}`;
};

// --- Component ---

const MattersTableView: React.FC<MattersTableViewProps> = ({
  matters,
  loading,
  onMatterClick,
  searchQuery = '',
  onRefreshNeeded
}) => {
  const { keycloak } = useKeycloak();
  const [selectedMatters, setSelectedMatters] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<keyof MatterDetailed>('matter_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [nextScheduledEvents, setNextScheduledEvents] = useState<NextScheduledEvents>({});
  const [enrichmentData, setEnrichmentData] = useState<EnrichmentData>({});
  const [enrichmentLoading, setEnrichmentLoading] = useState(true);

  // Archive modal state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveNotes, setArchiveNotes] = useState('');
  const [keepVisible, setKeepVisible] = useState(true);

  // Sort matters
  const sortedMatters = useMemo(() => {
    if (!Array.isArray(matters)) return [];
    const sorted = [...matters];
    sorted.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [matters, sortField, sortDirection]);

  // Stable matter IDs key — only re-fetch when the actual set of matter IDs changes, not on sort/re-render
  const matterIdsKey = useMemo(() => {
    if (!Array.isArray(matters) || matters.length === 0) return '';
    return matters.map(m => m.id).sort().join(',');
  }, [matters]);

  // Track previous IDs to avoid redundant fetches
  const prevIdsRef = useRef('');

  // Fetch calendar events + enrichment data in parallel, triggered only when matter IDs change
  useEffect(() => {
    if (!matterIdsKey) { setEnrichmentLoading(false); return; }
    if (!keycloak?.token || matterIdsKey === prevIdsRef.current) return;
    prevIdsRef.current = matterIdsKey;

    const ids = matterIdsKey.split(',');
    let cancelled = false;

    const fetchAll = async () => {
      setEnrichmentLoading(true);

      // Fire enrichment + all calendar requests in parallel
      const enrichmentPromise = fetch(`/api/v1/matters/list-enrichment?matter_ids=${ids.join(',')}`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      }).then(res => res.ok ? res.json() : null).catch(() => null);

      const calendarPromises = ids.map(async (id) => {
        try {
          const [courtRes, meetingRes] = await Promise.all([
            fetch(`/api/v1/calendars/matters/${id}/events?next_only=true&event_types=court-hearing,court-date&limit=1`, {
              headers: { Authorization: `Bearer ${keycloak.token}` },
            }),
            fetch(`/api/v1/calendars/matters/${id}/events?next_only=true&event_types=meeting,consultation,appointment&limit=1`, {
              headers: { Authorization: `Bearer ${keycloak.token}` },
            }),
          ]);
          const entry: NextScheduledEvents[string] = {};
          if (courtRes.ok) { const d = await courtRes.json(); if (d.events?.[0]) entry.nextCourt = d.events[0]; }
          if (meetingRes.ok) { const d = await meetingRes.json(); if (d.events?.[0]) entry.nextMeeting = d.events[0]; }
          return [id, entry] as const;
        } catch {
          return [id, {}] as const;
        }
      });

      const [enrichmentResult, ...calendarResults] = await Promise.all([enrichmentPromise, ...calendarPromises]);

      if (cancelled) return;

      // Single batched state update — no double re-render
      const events: NextScheduledEvents = {};
      for (const [id, entry] of calendarResults) {
        events[id] = entry;
      }
      setNextScheduledEvents(events);
      setEnrichmentData(enrichmentResult?.enrichments || {});
      setEnrichmentLoading(false);
    };

    fetchAll();

    return () => { cancelled = true; };
  }, [matterIdsKey, keycloak?.token]);

  const handleSort = (field: keyof MatterDetailed) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = () => {
    if (selectedMatters.size === sortedMatters.length) {
      setSelectedMatters(new Set());
    } else {
      setSelectedMatters(new Set(sortedMatters.map(m => m.id)));
    }
  };

  const handleSelectMatter = (matterId: string) => {
    const newSelected = new Set(selectedMatters);
    if (newSelected.has(matterId)) newSelected.delete(matterId);
    else newSelected.add(matterId);
    setSelectedMatters(newSelected);
  };

  const handleArchiveClick = () => {
    setArchiveReason('');
    setArchiveNotes('');
    setKeepVisible(true);
    setShowArchiveModal(true);
  };

  const handleArchiveSubmit = async () => {
    if (!archiveReason) return;

    setArchiveLoading(true);
    try {
      const matterIds = Array.from(selectedMatters);
      const errors: string[] = [];

      await Promise.all(
        matterIds.map(async (matterId) => {
          try {
            const response = await fetch(`/api/v1/matters/${matterId}/archive`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${keycloak?.token}`
              },
              body: JSON.stringify({
                archive_reason: archiveReason,
                archive_notes: archiveNotes,
                keep_visible: keepVisible
              })
            });

            if (!response.ok) {
              const errorText = await response.text();
              errors.push(`Matter ${matterId}: ${response.statusText}`);
              console.error(`Failed to archive matter ${matterId}:`, errorText);
            }
          } catch (error) {
            errors.push(`Matter ${matterId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
            console.error(`Failed to archive matter ${matterId}:`, error);
          }
        })
      );

      if (errors.length > 0) {
        console.warn('⚠️ Archive completed with errors:', errors);
        alert(`${matterIds.length - errors.length}/${matterIds.length} matters archived successfully.\n${errors.length} failed: ${errors.join('\n')}`);
      } else {
        console.log(`✅ All ${matterIds.length} matters archived successfully`);
      }

      // Clear selection and close modal
      setSelectedMatters(new Set());
      setShowArchiveModal(false);

      // Trigger refresh if callback provided
      if (onRefreshNeeded) {
        await onRefreshNeeded();
      }
    } catch (error) {
      console.error('Archive operation failed:', error);
      alert('Failed to archive engagements. Please try again.');
    } finally {
      setArchiveLoading(false);
    }
  };

  const thBase = 'px-3 py-2.5 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider';
  const thSortable = `${thBase} cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600`;
  const sortArrow = (field: keyof MatterDetailed) =>
    sortField === field ? <span className="ml-0.5">{sortDirection === 'asc' ? '↑' : '↓'}</span> : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading engagements...</span>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Bulk actions bar */}
      {selectedMatters.size > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-700 px-4 py-2.5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800 dark:text-blue-200">
              {selectedMatters.size} engagement{selectedMatters.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-2">
              <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Export</button>
              <button
                onClick={handleArchiveClick}
                disabled={archiveLoading}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {archiveLoading ? 'Archiving...' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              {/* Checkbox */}
              <th className="px-3 py-2.5 text-left w-10">
                <input
                  type="checkbox"
                  checked={selectedMatters.size === sortedMatters.length && sortedMatters.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              {/* Engagement Details */}
              <th className={`${thSortable} w-36`} onClick={() => handleSort('matter_number')}>
                Engagement Details {sortArrow('matter_number')}
              </th>
              {/* Description */}
              <th className={`${thSortable} w-52`} onClick={() => handleSort('title')}>
                Description {sortArrow('title')}
              </th>
              {/* Priority */}
              <th className={`${thSortable} w-20 text-center`} onClick={() => handleSort('priority' as keyof MatterDetailed)}>
                Priority {sortArrow('priority' as keyof MatterDetailed)}
              </th>
              {/* People */}
              <th className={`${thBase} w-28`}>People</th>
              {/* Health */}
              <th className={`${thBase} w-32`}>Health</th>
              {/* Next Scheduled */}
              <th className={`${thBase} w-36`}>Next Scheduled</th>
              {/* Financial */}
              <th className={`${thSortable} w-28`} onClick={() => handleSort('estimated_value')}>
                Financial {sortArrow('estimated_value')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedMatters.map((matter, idx) => {
              const enrich = enrichmentData[matter.id];
              const ts = enrich?.task_summary;
              const pri = getPriorityConfig(matter.priority);
              const risk = getRiskConfig(enrich?.risk_level);
              const solDays = getDaysUntilSOL((matter.important_dates as any)?.statute_of_limitations);
              const solUrgent = solDays !== null && solDays < 90;

              return (
                <tr
                  key={matter.id}
                  className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                  style={{ '--row-index': idx } as React.CSSProperties}
                  onClick={() => onMatterClick(matter)}
                >
                  {/* Checkbox */}
                  <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedMatters.has(matter.id)}
                      onChange={() => handleSelectMatter(matter.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>

                  {/* Engagement Details (absorbs openDate + age) */}
                  <td className="px-3 py-3 text-sm">
                    <div className="font-medium text-xs text-gray-900 dark:text-white leading-tight">
                      {highlightText(matter.displayNumber, searchQuery)}
                    </div>
                    <div className="mt-0.5">
                      <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${getStatusColor(matter.status || 'pending')}`}>
                        {matter.status ? matter.status.charAt(0).toUpperCase() + matter.status.slice(1).replace('_', ' ') : 'Pending'}
                      </span>
                    </div>
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                      {formatDate(matter.openDate)} · <span className="font-medium">{getAgeDays(matter.openDate)}d</span>
                    </div>
                  </td>

                  {/* Description (shrunk) */}
                  <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">
                    <div className="space-y-0.5">
                      <div
                        className="font-medium text-xs leading-tight"
                        style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        title={matter.title}
                      >
                        {highlightText(matter.title, searchQuery)}
                      </div>
                      {matter.practice_area && (
                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full">
                          {matter.practice_area}
                        </span>
                      )}
                      {matter.description && matter.description !== matter.title && (
                        <div
                          className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight"
                          style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                          title={matter.description}
                        >
                          {highlightText(matter.description, searchQuery)}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Priority */}
                  <td className="px-2 py-3 text-center">
                    <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${pri.badge}`}>
                      {pri.label}
                    </span>
                  </td>

                  {/* People */}
                  <td className="px-3 py-3 text-xs">
                    <div className="text-gray-900 dark:text-white truncate" title={matter.client?.name}>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">C:</span>{' '}
                      {matter.client?.name ? highlightText(matter.client.name, searchQuery) : <span className="text-gray-400">—</span>}
                    </div>
                    <div className="text-gray-900 dark:text-white truncate mt-0.5" title={matter.responsibleAttorney?.name}>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">A:</span>{' '}
                      {matter.responsibleAttorney?.name ? highlightText(matter.responsibleAttorney.name, searchQuery) : <span className="text-gray-400">—</span>}
                    </div>
                  </td>

                  {/* Health */}
                  <td className="px-3 py-3 text-xs" style={{ minHeight: '3.5rem' }}>
                    {enrichmentLoading ? (
                      <div className="space-y-1.5" style={{ minHeight: '2.5rem' }}>
                        <div className="h-2.5 bg-gray-200 dark:bg-gray-600 rounded w-16 animate-pulse" />
                        <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded w-full animate-pulse" />
                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-12 animate-pulse" />
                      </div>
                    ) : ts && ts.total > 0 ? (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-gray-500 dark:text-gray-400">
                            Tasks: <span className="font-medium text-gray-700 dark:text-gray-300">{ts.done}/{ts.total}</span>
                          </span>
                          {ts.overdue > 0 && (
                            <span className="text-red-600 dark:text-red-400 font-semibold flex items-center gap-0.5">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              {ts.overdue} overdue
                            </span>
                          )}
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all ${ts.overdue > 0 ? 'bg-red-500' : ts.done === ts.total ? 'bg-green-500' : 'bg-blue-600'}`}
                            style={{ width: `${(ts.done / ts.total) * 100}%` }}
                          />
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">Risk:</span>
                          <div className={`w-1.5 h-1.5 rounded-full ${risk.color}`} />
                          <span className="text-[10px] text-gray-600 dark:text-gray-300 font-medium">{risk.label}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">No tasks</span>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">Risk:</span>
                          <div className={`w-1.5 h-1.5 rounded-full ${risk.color}`} />
                          <span className="text-[10px] text-gray-600 dark:text-gray-300 font-medium">{risk.label}</span>
                        </div>
                      </div>
                    )}
                  </td>

                  {/* Next Scheduled + SOL */}
                  <td className="px-3 py-3 text-xs">
                    <div className="space-y-0.5">
                      <div>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">Court:</span>{' '}
                        {nextScheduledEvents[matter.id]?.nextCourt ? (
                          <span className="text-gray-900 dark:text-white font-medium">
                            {nextScheduledEvents[matter.id]!.nextCourt!.start_time && formatEventDate(nextScheduledEvents[matter.id]!.nextCourt!.start_time)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">Mtg:</span>{' '}
                        {nextScheduledEvents[matter.id]?.nextMeeting ? (
                          <span className="text-gray-900 dark:text-white font-medium">
                            {nextScheduledEvents[matter.id]!.nextMeeting!.start_time && formatEventDate(nextScheduledEvents[matter.id]!.nextMeeting!.start_time)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                      {(matter.important_dates as any)?.statute_of_limitations && (
                        <div className={`text-[10px] mt-0.5 ${solUrgent ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                          SOL: {formatDate((matter.important_dates as any).statute_of_limitations)}
                          {solDays !== null && <span className="ml-0.5">({solDays}d)</span>}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Financial */}
                  <td className="px-3 py-3 text-xs" style={{ minHeight: '3.5rem' }}>
                    <div className="space-y-1">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {matter.estimated_value ? formatCurrency(matter.estimated_value) : <span className="text-gray-400 font-normal">—</span>}
                      </div>
                      {enrichmentLoading ? (
                        <div className="space-y-1.5" style={{ minHeight: '1.5rem' }}>
                          <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-14 animate-pulse" />
                          <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-10 animate-pulse" />
                        </div>
                      ) : enrich?.budget ? (
                        <div>
                          <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400">
                            <span>Budget</span>
                            <span className={`font-medium ${enrich.budget.status === 'exceeded' ? 'text-red-600 dark:text-red-400' : ''}`}>
                              {formatCurrency(enrich.budget.total_budget)}
                            </span>
                          </div>
                          {enrich.budget.status === 'exceeded' && (
                            <div className="text-[10px] text-red-600 dark:text-red-400 font-semibold">Exceeded</div>
                          )}
                        </div>
                      ) : null}
                      {!enrichmentLoading && enrich?.retainer_balance != null && (
                        <div className="text-[10px] text-gray-500 dark:text-gray-400">
                          Trust: <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(enrich.retainer_balance)}</span>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {sortedMatters.length === 0 && (
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No engagements found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by creating a new engagement.
          </p>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => !archiveLoading && setShowArchiveModal(false)} />

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4v2m0-6a9 9 0 110-18 9 9 0 010 18z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                        Archive {selectedMatters.size} Engagement{selectedMatters.size !== 1 ? 's' : ''}
                      </h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowArchiveModal(false)}
                    disabled={archiveLoading}
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none disabled:opacity-50"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Archive Reason <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={archiveReason}
                      onChange={(e) => setArchiveReason(e.target.value)}
                      disabled={archiveLoading}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                    >
                      <option value="">Select a reason...</option>
                      <option value="case_settled">Case Settled</option>
                      <option value="case_completed">Case Completed</option>
                      <option value="case_dismissed">Case Dismissed</option>
                      <option value="client_requested">Client Requested</option>
                      <option value="transferred_counsel">Transferred to Other Counsel</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Archive Notes
                    </label>
                    <textarea
                      value={archiveNotes}
                      onChange={(e) => setArchiveNotes(e.target.value)}
                      disabled={archiveLoading}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                      placeholder="Additional notes about archiving..."
                    />
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-4">
                    <div className="flex items-start">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
                          Keep Visible After Archive?
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                          Choose whether these closed engagements remain visible in your main engagements list.
                        </p>

                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="visibility"
                              checked={keepVisible === true}
                              onChange={() => setKeepVisible(true)}
                              disabled={archiveLoading}
                              className="form-radio h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 disabled:opacity-50"
                            />
                            <span className="ml-2 text-sm text-blue-900 dark:text-blue-100">
                              <strong>Yes</strong> - Keep visible (shows as "Closed" status)
                            </span>
                          </label>

                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="visibility"
                              checked={keepVisible === false}
                              onChange={() => setKeepVisible(false)}
                              disabled={archiveLoading}
                              className="form-radio h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 disabled:opacity-50"
                            />
                            <span className="ml-2 text-sm text-blue-900 dark:text-blue-100">
                              <strong>No</strong> - Hide from main list
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleArchiveSubmit}
                  disabled={archiveLoading || !archiveReason}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {archiveLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Archiving...
                    </>
                  ) : (
                    'Archive Engagements'
                  )}
                </button>
                <button
                  onClick={() => setShowArchiveModal(false)}
                  disabled={archiveLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MattersTableView;
